import './App.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import routes from './routes/Index';
import { ThemeProvider } from '@mui/material';
import appTheme from './theme/Theme';
import { config } from './shared/constants/WagmiConfig';
import { SnackbarProvider } from 'notistack';
import { PrivyProvider, User, getAccessToken } from '@privy-io/react-auth';
import { authenticate } from './api/wallet.api';
import { WagmiProvider } from '@privy-io/wagmi';
import { activeChain } from './shared/utils/CheckNetwork';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { VirtualBalanceProvider } from './context/VirtualBalanceContext';
import { ConfirmProvider } from 'material-ui-confirm';
import { NotificationProvider } from './providers/NotificationProvider';
import OneSignalProvider from './providers/OneSignalProvider';
import { AppProvider } from './context/AppContext';

export const queryClient = new QueryClient();

const handleLogin = async (user: User) => {
  try {
    const accessToken = await getAccessToken();

    if (accessToken) {
      const bearerToken = await authenticate(accessToken, user.linkedAccounts);
      sessionStorage.setItem('token', bearerToken.accessToken);

      queryClient.invalidateQueries();

      const beforeLoginUrl = sessionStorage.getItem('beforeLogin');
      if (beforeLoginUrl) {
        routes.navigate(beforeLoginUrl);
        sessionStorage.removeItem('beforeLogin');
      }
    }
  } catch (error: any) {
    console.error(error);
  }
};

const App = () => {
  const initGTM = () => {
    if (!process.env.REACT_APP_GTM_ID) return;
    const tagManagerArgs = {
      gtmId: process.env.REACT_APP_GTM_ID,
    };

    if (tagManagerArgs && tagManagerArgs.gtmId) {
      TagManager.initialize(tagManagerArgs);
    }
  };

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      initGTM();
    }
  }, []);

  return (
    <PrivyProvider
      appId={process.env.REACT_APP_PRIVY_APP_ID!}
      onSuccess={(user) => handleLogin(user)}
      config={{
        loginMethods: ['email', 'wallet'],
        embeddedWallets: {
          createOnLogin: 'users-without-wallets',
        },
        defaultChain: activeChain,
        appearance: {
          theme: 'light',
          accentColor: '#43BDCE',
        },
        externalWallets: {
          coinbaseWallet: {
            connectionOptions: 'all',
          },
        },
      }}
    >
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <OneSignalProvider>
          <NotificationProvider>
            <QueryClientProvider client={queryClient}>
              <WagmiProvider config={config}>
                <ThemeProvider theme={appTheme}>
                  <ConfirmProvider>
                    <VirtualBalanceProvider>
                      <AppProvider>
                        <RouterProvider router={routes} />
                      </AppProvider>
                    </VirtualBalanceProvider>
                  </ConfirmProvider>
                </ThemeProvider>
              </WagmiProvider>
            </QueryClientProvider>
          </NotificationProvider>
        </OneSignalProvider>
      </SnackbarProvider>
    </PrivyProvider>
  );
};

export default App;
